import { createFileRoute } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, FeatureGrid, CtaBand } from "@/components/MarketingShell";

export const Route = createFileRoute("/custom-reports")({
  component: CustomReportsPage,
  head: () => ({
    meta: [
      { title: "Custom Report Builder for Security Teams — MissionReady" },
      { name: "description", content: "Design your own field report templates with drag-and-drop fields, photos, signatures, and conditional logic. Assign to specific sites or your whole organization." },
      ...socialMeta({
        title: "Custom Report Builder for Security Teams — MissionReady",
        description: "Drag-and-drop custom report templates with photos, signatures, and per-site assignment. Replace paper checklists with branded, client-ready reports.",
        url: "https://missionready.dev/custom-reports",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/custom-reports" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Custom Reports",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        description: "Drag-and-drop builder for custom field report templates with per-site assignment, signatures, photos, and branded PDF exports.",
        url: "https://missionready.dev/custom-reports",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Drag-and-drop template builder",
          "Text, number, date, select, checkbox, signature, photo fields",
          "Draft / Published / Archived workflow",
          "Per-site or org-wide assignment",
          "Submission history per template",
          "Branded PDF exports",
        ],
      }),
    }],
  }),
});

const FEATURES = [
  { icon: "🧩", title: "Drag-and-drop builder", desc: "Compose any report from text, number, date, select, checkbox, signature, and photo fields — no developer required." },
  { icon: "✍️", title: "Signatures & photo capture", desc: "Collect officer or client signatures and on-scene photos as native fields, stamped with GPS and timestamp." },
  { icon: "📚", title: "Draft, publish, archive", desc: "Iterate safely. Keep drafts private until you publish, and archive old templates without losing history." },
  { icon: "🎯", title: "Per-site or org-wide assignment", desc: "Assign a template to specific sites, or apply it across every site in your organization with one toggle." },
  { icon: "🗂️", title: "Submission history", desc: "Every filled report is stored against its template and site, ready to review, export, or attach to invoices." },
  { icon: "📤", title: "Branded PDF exports", desc: "Render any submission as a polished, client-ready PDF using your organization's logo and colors." },
];

function CustomReportsPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Custom Reports"
        title={<>Build the exact reports <span className="text-primary">your clients ask for.</span></>}
        subtitle="Drag-and-drop custom report templates with photos, signatures, and per-site assignment — replace paper checklists with branded, client-ready reports."
      />
      <FeatureGrid items={FEATURES} heading="Custom report builder capabilities" />
      <CtaBand title="Stop forcing your work into someone else's template" subtitle="Build your first custom report in minutes — included on Pro and Enterprise." />
    </MarketingShell>
  );
}
